/**
 * current-wire 适配边界：把当前 wire 响应严格校验并适配为 CanonicalAISession。
 *
 * 契约守门（ADR-client-001 `docs/client/adrs/001-wire-contract-sunset.md`）：
 * - 本层输入保持 `unknown` 宽入、输出严格 Canonical，这是适配器职责，不是双读债。
 * - 任何「同一字段两处读取 / 同一事件两种格式」的迁移期兼容，必须登记到该 ADR 的
 *   退出台账（含退出条件与截止日期，最长 4 周），否则禁止合入。
 * - 已声明字段禁止再经 `extensions` 桶二次读取（W1 已闭环 `editChange` 为先例）。
 */
import type { AISession } from '@/types/ai-session';
import {
  isCanonicalStageStatus,
  type AlignmentCanonicalSession,
  type AssetCanonicalSession,
  type CanonicalAISession,
  type StoryboardCanonicalSession,
} from './types';
import { parseAssetCanonicalPayload } from './asset';
import { parseStoryboardCanonicalPayload } from './storyboard';
import { parseAlignmentCanonicalPayload } from './alignment';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function adaptAlignment(session: AISession): AlignmentCanonicalSession {
  return {
    ...session,
    sessionType: 1,
    payload: parseAlignmentCanonicalPayload(session.payload),
  } as AlignmentCanonicalSession;
}

function adaptAsset(session: AISession): AssetCanonicalSession {
  return {
    ...session,
    sessionType: 3,
    payload: parseAssetCanonicalPayload(session.payload),
  } as AssetCanonicalSession;
}

function adaptStoryboard(session: AISession): StoryboardCanonicalSession {
  return {
    ...session,
    sessionType: 4,
    payload: parseStoryboardCanonicalPayload(session.payload),
  } as StoryboardCanonicalSession;
}

export function adaptCurrentWireSession(value: unknown): CanonicalAISession {
  const session = value;
  if (!isRecord(session)) {
    throw new Error('AI Session 响应必须是对象');
  }
  if (typeof session.sessionId !== 'string' || !session.sessionId.trim()) {
    throw new Error('AI Session 响应缺少有效的 sessionId');
  }
  for (const field of ['userId', 'sourceId', 'createdAt', 'updatedAt'] as const) {
    if (typeof session[field] !== 'string' || !session[field].trim()) {
      throw new Error(`AI Session 响应缺少有效的 ${field}`);
    }
  }
  if (
    session.projectId !== null &&
    (typeof session.projectId !== 'string' || !session.projectId.trim())
  ) {
    throw new Error('AI Session 响应的 projectId 必须是非空字符串或 null');
  }
  for (const field of ['stageStatus', 'stageVersion', 'sourceSessionId'] as const) {
    if (!(field in session)) {
      throw new Error(`AI Session 响应缺少顶层生命周期字段 ${field}`);
    }
  }
  if (session.sourceType !== 1) {
    throw new Error('AI Session 响应缺少有效的 sourceType');
  }
  if (typeof session.status !== 'number' || ![1, 2, 3, 4].includes(session.status)) {
    throw new Error('AI Session 响应缺少有效的 status');
  }
  if (
    session.stageVersion !== undefined &&
    session.stageVersion !== null &&
    (typeof session.stageVersion !== 'number' || !Number.isFinite(session.stageVersion))
  ) {
    throw new Error('AI Session 响应的 stageVersion 必须是有限数字或 null');
  }
  if (
    session.revision !== undefined &&
    (typeof session.revision !== 'number' ||
      !Number.isInteger(session.revision) ||
      session.revision < 0)
  ) {
    throw new Error('AI Session 响应的 revision 必须是非负整数');
  }
  if (!isRecord(session.payload)) {
    throw new Error('AI Session 响应的 payload 必须是对象');
  }
  if (session.stageStatus !== null && readString(session.stageStatus) === null) {
    throw new Error('AI Session 响应的 stageStatus 必须是非空字符串或 null');
  }
  if (typeof session.stageStatus === 'string' && !isCanonicalStageStatus(session.stageStatus)) {
    throw new Error(`不支持的 AI Session 阶段状态：${session.stageStatus}`);
  }
  if (session.sourceSessionId !== null && readString(session.sourceSessionId) === null) {
    throw new Error('AI Session 响应的 sourceSessionId 必须是非空字符串或 null');
  }
  if (
    session.deletedAt !== undefined &&
    session.deletedAt !== null &&
    typeof session.deletedAt !== 'string'
  ) {
    throw new Error('AI Session 响应的 deletedAt 必须是字符串或 null');
  }
  if (
    (session.sessionType === 3 || session.sessionType === 4) &&
    session.sourceSessionId === null
  ) {
    throw new Error(`AI Session 类型 ${session.sessionType} 缺少 sourceSessionId`);
  }
  const validatedSession = {
    ...session,
    deletedAt: session.deletedAt ?? null,
  } as unknown as AISession;
  if (session.sessionType === 1) return adaptAlignment(validatedSession);
  if (session.sessionType === 3) return adaptAsset(validatedSession);
  if (session.sessionType === 4) return adaptStoryboard(validatedSession);
  throw new Error(`不支持的 AI Session 类型：${session.sessionType}`);
}
